import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useMemo } from 'react';
import { Link } from 'ui-components/common/Link';
import { isPC } from 'constants/layout';
import {
  ExploreNavigationMenu,
  ExploreNavigationMenuPath,
  ExploreSubMenu,
  getExploreSubMenus,
} from '@diritto/common/navigation/explore';
import { CategoryNavigationMenu, CategoryNavigationMenuPath } from '@diritto/common/navigation/constant';

const BorderRadius = 8;

export const ExploreSubTab = ({
  selectSubMenu,
  exploreMenu,
  categoryNavMenu,
  className,
}: {
  exploreMenu: ExploreNavigationMenu;
  categoryNavMenu: CategoryNavigationMenu;
  selectSubMenu?: ExploreSubMenu;
  className?: string;
}) => {
  const exploreSubMenus = getExploreSubMenus(exploreMenu);
  const selectedIndex = useMemo(
    () => exploreSubMenus.findIndex(item => item.exploreSubMenu === selectSubMenu),
    [exploreSubMenus, selectSubMenu],
  );

  const path = useMemo(
    () => `/explore/${ExploreNavigationMenuPath[exploreMenu]}/${CategoryNavigationMenuPath[categoryNavMenu]}`,
    [categoryNavMenu, exploreMenu],
  );

  if (exploreSubMenus.length === 0) {
    return null;
  }

  return (
    <StyledWrapper className={className}>
      <StyledList>
        {exploreSubMenus.map((it, index) => (
          <StyledItem key={it.exploreSubMenu} isSelected={selectedIndex === index} selectedIndex={selectedIndex}>
            <Link href={`${path}?exploreSubMenu=${it.exploreSubMenu}`}>
              <StyledTab isSelected={selectedIndex === index}>{it.text}</StyledTab>
            </Link>
          </StyledItem>
        ))}
      </StyledList>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  display: flex;
`;

const StyledList = styled.ul`
  display: flex;
  background-color: ${Color.Gray200};
  width: 100%;
  height: 35px;
  border-radius: ${BorderRadius}px;
  overflow: hidden;
  padding: 2px;
  margin: 0 40px;

  @media (${isPC}) {
    max-width: 316px;
  }
`;

const StyledTab = styled.p(
  ({ isSelected }: { isSelected: boolean }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 20px;
    border-right: 1px solid ${Color.Gray300};
    color: ${isSelected ? Color.Black : Color.Gray600};
    font-weight: ${isSelected ? FontWeight.Bold : FontWeight.Medium};
    ${Typography.XXXSmall};
  `,
);

const StyledItem = styled.li(
  ({ isSelected = false, selectedIndex }: { isSelected?: boolean; selectedIndex: number }) => css`
    flex: 1;
    display: flex;
    justify-content: center;
    height: 100%;
    position: relative;
    border-radius: ${BorderRadius}px;
    overflow: hidden;

    background-color: ${isSelected ? Color.White : 'transparent'};

    & > a {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &:nth-of-type(${selectedIndex}) {
      ${StyledTab} {
        border-right: none;
      }
    }

    &:nth-of-type(${selectedIndex + 1}) {
      ${StyledTab} {
        border-right: none;
      }
    }

    &:nth-last-of-type(1) {
      ${StyledTab} {
        border-right: none;
      }
    }
  `,
);
